import React from "react";
import { HelpCircle } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

// Canonical, one-place-to-edit descriptions of every score surface so the
// same short explainer is shown wherever the score appears (Dashboard,
// Report screen, Milestone banner, exports).
export const SCORE_EXPLAINERS = {
  overall: {
    title: "Interview Performance",
    body:
      "How well you performed in THIS interview: delivery, clarity, pace, filler-word discipline, "
      + "STAR structure, confidence, answer completeness. Session quality only. Does not "
      + "factor in whether the content matches this specific role.",
  },
  readiness: {
    title: "Interview Readiness Score",
    body:
      "How ready you are for the REAL interview for this specific role: how well your answers, "
      + "quantified impact, and demonstrated competencies match the Job Description and expected "
      + "seniority. Weighted by the evidence-based blueprint built from your resume + JD.",
  },
  match: {
    title: "Job Match Score",
    body: "How well your resume matches this specific job description: skills, experience "
      + "signals, seniority, and industry fit combined.",
  },
  ats: {
    title: "ATS Score",
    body: "A deterministic structure check of your resume itself — contact info, standard "
      + "section headers, bullet points, dates, quantified results, and clean machine-readable "
      + "formatting. Independent of any job description: the same resume always scores the same.",
  },
};

/**
 * Tiny `?` chip next to a score label. Hover (desktop) or tap (mobile) reveals
 * a short explainer. Keep the label pass-through simple so any caller can
 * drop it into an existing row.
 */
export default function ScoreInfoTip({ variant = "overall", label = null, className = "" }) {
  const { title, body } = SCORE_EXPLAINERS[variant] || SCORE_EXPLAINERS.overall;
  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`What is ${label || title}?`}
            data-testid={`score-info-${variant}`}
            className={`inline-flex items-center justify-center h-4 w-4 rounded-full text-zinc-500 hover:text-zinc-200 focus:outline-none focus:ring-1 focus:ring-amber-400/40 transition-colors ${className}`}
          >
            <HelpCircle className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs bg-zinc-900 text-zinc-100 border border-zinc-800 shadow-xl px-3 py-2.5 text-[12px] leading-relaxed"
        >
          <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-1">
            {title}
          </div>
          <div className="text-zinc-300">{body}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
